// src/components/setup/SetupWizard.jsx — one-time first-run setup for a new shop.
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import { ACCENT, fromSettings, toPayload, BusinessFields, CurrencyFields, FeatureFields, DefaultFields } from '../../pages/settings/companyForm';

export default function SetupWizard({ onDone }) {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.get('/api/settings').then(r => r.data) });
  const { data: paymentMethods = [] } = useQuery({ queryKey: ['payment-methods'], queryFn: () => api.get('/api/payment-methods').then(r => r.data) });

  const [form, setForm] = useState(fromSettings(settings));
  const [step, setStep] = useState(0);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const finish = useMutation({
    mutationFn: () => api.put('/api/settings', { ...toPayload(form), setup_completed: true }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['settings'] }); onDone?.(); },
    onError: (err) => alert(err.response?.data?.error || 'Error saving setup'),
  });

  const steps = [
    { title: 'Welcome 👋', subtitle: 'Tell us about your business', body: <BusinessFields f={form} set={set} />, canNext: !!form.name.trim() },
    { title: 'Currency',   subtitle: 'How you price and convert',  body: <CurrencyFields f={form} set={set} />, canNext: true },
    { title: 'Features',   subtitle: 'Match the app to your shop',  body: <FeatureFields f={form} set={set} />, canNext: true },
    { title: 'Defaults',   subtitle: 'A few finishing touches',     body: <DefaultFields f={form} set={set} paymentMethods={paymentMethods} />, canNext: true },
  ];
  const cur = steps[step];
  const last = step === steps.length - 1;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'linear-gradient(135deg,#0f172a 0%,#134e4a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: 640, maxWidth: '100%', background: '#fff', borderRadius: 18, boxShadow: '0 24px 64px rgba(0,0,0,0.4)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
        {/* header */}
        <div style={{ padding: '20px 24px', borderBottom: '1.5px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg,${ACCENT},#1f7d68)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 18 }}>
                <span style={{ width: 4, height: 9, background: 'rgba(255,255,255,.9)', borderRadius: 2 }} />
                <span style={{ width: 4, height: 14, background: '#fff', borderRadius: 2 }} />
                <span style={{ width: 4, height: 18, background: 'rgba(255,255,255,.9)', borderRadius: 2 }} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0d0d0d' }}>MMM<span style={{ color: ACCENT }}>POS</span> Setup</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>Step {step + 1} of {steps.length}</div>
            </div>
          </div>
          {/* progress */}
          <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: i <= step ? ACCENT : '#e5e7eb', transition: 'background .25s' }} />
            ))}
          </div>
        </div>

        {/* body */}
        <div style={{ padding: 24, overflowY: 'auto' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>{cur.title}</div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 18 }}>{cur.subtitle}</div>
          {cur.body}
        </div>

        {/* footer */}
        <div style={{ padding: 18, borderTop: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
            style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 9, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: step === 0 ? 0.4 : 1 }}>
            Back
          </button>
          <div style={{ marginInlineStart: 'auto' }} />
          {!last ? (
            <button onClick={() => setStep(s => s + 1)} disabled={!cur.canNext}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: cur.canNext ? 1 : 0.5 }}>
              Next →
            </button>
          ) : (
            <button onClick={() => finish.mutate()} disabled={finish.isPending || !form.name.trim()}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (finish.isPending || !form.name.trim()) ? 0.5 : 1 }}>
              {finish.isPending ? 'Setting up…' : '✓ Finish & Launch'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
